import React, { useState, useEffect, useCallback } from 'react';
import {
  Eye, Settings, Play, AlertTriangle, CheckCircle, XCircle,
  Clock, Users, TrendingUp, RefreshCw, ChevronRight, Shield,
  Search, Filter, ArrowUpDown, Activity, UserX, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PTDetectionDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [flags, setFlags] = useState([]);
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState([]);
  const [selectedFlag, setSelectedFlag] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [reviewModal, setReviewModal] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);
  const token = localStorage.getItem('uf_token');

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, dashRes, cfgRes, runsRes] = await Promise.all([
        fetch(`${API}/api/pt-detection/stats`, { headers }),
        fetch(`${API}/api/pt-detection/dashboard${statusFilter ? `?status=${statusFilter}` : ''}`, { headers }),
        fetch(`${API}/api/pt-detection/config`, { headers }),
        fetch(`${API}/api/pt-detection/runs`, { headers }),
      ]);
      setStats(await statsRes.json());
      const dashData = await dashRes.json();
      setFlags(dashData.items || []);
      setConfig(await cfgRes.json());
      setRuns(await runsRes.json());
    } catch (e) {
      console.error('Failed to load PT detection data', e);
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const res = await fetch(`${API}/api/pt-detection/analyze`, { method: 'POST', headers });
      const data = await res.json();
      if (data.status === 'completed') {
        fetchData();
      }
    } catch (e) {
      console.error('Analysis failed', e);
    }
    setAnalyzing(false);
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await fetch(`${API}/api/pt-detection/config`, {
        method: 'PUT', headers, body: JSON.stringify(config),
      });
    } catch (e) {
      console.error('Config save failed', e);
    }
    setSavingConfig(false);
  };

  const submitReview = async () => {
    if (!reviewModal) return;
    try {
      await fetch(`${API}/api/pt-detection/flag/${reviewModal.flagId}/review`, {
        method: 'POST', headers,
        body: JSON.stringify({ decision: reviewModal.decision, notes: reviewModal.notes }),
      });
      setReviewModal(null);
      setSelectedFlag(null);
      fetchData();
    } catch (e) {
      console.error('Review failed', e);
    }
  };

  const loadFlagDetail = async (flagId) => {
    try {
      const res = await fetch(`${API}/api/pt-detection/flag/${flagId}`, { headers });
      setSelectedFlag(await res.json());
    } catch (e) {
      console.error('Failed to load flag detail', e);
    }
  };

  const getRiskColor = (score) => {
    if (score >= 80) return 'text-red-400';
    if (score >= 60) return 'text-orange-400';
    if (score >= 40) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getRiskBg = (score) => {
    if (score >= 80) return 'bg-red-500/20 border-red-500/30';
    if (score >= 60) return 'bg-orange-500/20 border-orange-500/30';
    if (score >= 40) return 'bg-yellow-500/20 border-yellow-500/30';
    return 'bg-green-500/20 border-green-500/30';
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      monitoring: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      confirmed: 'bg-red-500/20 text-red-400 border-red-500/30',
      dismissed: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    };
    return map[status] || map.pending;
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pt-detection-loading">
        <RefreshCw className="w-6 h-6 text-uf-red animate-spin" />
        <span className="ml-3 text-uf-text-muted">Loading intelligence engine...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pt-detection-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-uf-text tracking-tight">
            PT Detection Intelligence
          </h1>
          <p className="text-uf-text-muted text-sm mt-1">
            Automated detection of unauthorized personal training activity
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={runAnalysis}
            disabled={analyzing}
            className="flex items-center gap-2 px-4 py-2 bg-uf-red hover:bg-uf-red-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid="run-analysis-btn"
          >
            {analyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {analyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-uf-surface rounded-lg p-1 border border-uf-border">
        {[
          { id: 'dashboard', label: 'Dashboard', icon: Eye },
          { id: 'settings', label: 'Settings', icon: Settings },
          { id: 'history', label: 'Run History', icon: Clock },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors flex-1 justify-center ${
              activeTab === tab.id
                ? 'bg-uf-surface-hi text-uf-text'
                : 'text-uf-text-muted hover:text-uf-text'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && (
        <DashboardView
          stats={stats}
          flags={flags}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          selectedFlag={selectedFlag}
          setSelectedFlag={setSelectedFlag}
          loadFlagDetail={loadFlagDetail}
          setReviewModal={setReviewModal}
          getRiskColor={getRiskColor}
          getRiskBg={getRiskBg}
          getStatusBadge={getStatusBadge}
        />
      )}

      {activeTab === 'settings' && config && (
        <SettingsView
          config={config}
          setConfig={setConfig}
          saveConfig={saveConfig}
          savingConfig={savingConfig}
        />
      )}

      {activeTab === 'history' && (
        <RunHistoryView runs={runs} />
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="review-modal">
          <div className="bg-uf-surface border border-uf-border rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-uf-text mb-4">
              {reviewModal.decision === 'confirmed' ? 'Confirm Unauthorized PT' :
               reviewModal.decision === 'dismissed' ? 'Dismiss Flag' : 'Continue Monitoring'}
            </h3>
            <p className="text-uf-text-muted text-sm mb-4">
              {reviewModal.decision === 'confirmed'
                ? 'This will mark the member as a confirmed unauthorized PT. The detection engine will strengthen similar pattern detection in future analysis runs.'
                : reviewModal.decision === 'dismissed'
                ? 'This will dismiss the flag as a false positive. The detection engine will reduce the weight of similar patterns in future.'
                : 'This will keep the member under active monitoring. The engine will continue tracking their activity.'}
            </p>
            <textarea
              value={reviewModal.notes}
              onChange={e => setReviewModal({...reviewModal, notes: e.target.value})}
              placeholder="Add review notes (optional)..."
              className="w-full bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm h-24 resize-none mb-4"
              style={{ color: '#EDEDED' }}
              data-testid="review-notes-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setReviewModal(null)}
                className="flex-1 px-4 py-2 bg-uf-surface-hi text-uf-text-muted rounded-lg text-sm hover:text-uf-text"
              >
                Cancel
              </button>
              <button
                onClick={submitReview}
                className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium text-white ${
                  reviewModal.decision === 'confirmed' ? 'bg-red-600 hover:bg-red-700' :
                  reviewModal.decision === 'dismissed' ? 'bg-gray-600 hover:bg-gray-700' :
                  'bg-blue-600 hover:bg-blue-700'
                }`}
                data-testid="submit-review-btn"
              >
                Confirm Decision
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ──────────────── DASHBOARD VIEW ────────────────

const DashboardView = ({
  stats, flags, statusFilter, setStatusFilter, selectedFlag, setSelectedFlag,
  loadFlagDetail, setReviewModal, getRiskColor, getRiskBg, getStatusBadge
}) => {
  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-cards">
          <StatCard
            label="Pending Review"
            value={stats.pending}
            icon={AlertTriangle}
            color="text-yellow-400"
            bg="bg-yellow-500/10"
          />
          <StatCard
            label="Monitoring"
            value={stats.monitoring}
            icon={Eye}
            color="text-blue-400"
            bg="bg-blue-500/10"
          />
          <StatCard
            label="Confirmed"
            value={stats.confirmed}
            icon={UserX}
            color="text-red-400"
            bg="bg-red-500/10"
          />
          <StatCard
            label="Avg Risk Score"
            value={stats.average_risk_score}
            icon={TrendingUp}
            color="text-orange-400"
            bg="bg-orange-500/10"
          />
        </div>
      )}

      {/* Last Run Info */}
      {stats?.last_run && (
        <div className="bg-uf-surface border border-uf-border rounded-xl p-4 flex flex-wrap items-center gap-4 text-sm" data-testid="last-run-info">
          <Activity className="w-4 h-4 text-uf-red" />
          <span className="text-uf-text-muted">Last analysis:</span>
          <span className="text-uf-text font-medium">
            {new Date(stats.last_run.started_at).toLocaleString('en-GB')}
          </span>
          <span className="text-uf-text-muted">|</span>
          <span className="text-uf-text">{stats.last_run.logs_processed} logs processed</span>
          <span className="text-uf-text-muted">|</span>
          <span className="text-uf-text">{stats.last_run.members_analyzed} members analyzed</span>
          <span className="text-uf-text-muted">|</span>
          <span className="text-uf-text">{stats.last_run.flags_created} new flags</span>
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-uf-text-muted" />
        {['', 'pending', 'monitoring', 'confirmed', 'dismissed'].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
              statusFilter === s
                ? 'bg-uf-red text-white border-uf-red'
                : 'bg-uf-surface text-uf-text-muted border-uf-border hover:text-uf-text'
            }`}
            data-testid={`filter-${s || 'all'}`}
          >
            {s ? s.charAt(0).toUpperCase() + s.slice(1) : 'All Active'}
          </button>
        ))}
      </div>

      {/* Flags List + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Flags List */}
        <div className={`${selectedFlag ? 'lg:col-span-1' : 'lg:col-span-3'} space-y-3`}>
          {flags.length === 0 ? (
            <div className="bg-uf-surface border border-uf-border rounded-xl p-8 text-center" data-testid="no-flags">
              <Shield className="w-12 h-12 text-green-400/40 mx-auto mb-3" />
              <p className="text-uf-text font-medium">No flags detected</p>
              <p className="text-uf-text-muted text-sm mt-1">
                The detection engine has not identified any suspicious patterns. Run an analysis to scan latest access data.
              </p>
            </div>
          ) : (
            flags.map(flag => (
              <div
                key={flag.id}
                onClick={() => loadFlagDetail(flag.id)}
                className={`bg-uf-surface border rounded-xl p-4 cursor-pointer hover:border-uf-red/50 transition-colors ${
                  selectedFlag?.id === flag.id ? 'border-uf-red' : 'border-uf-border'
                }`}
                data-testid={`flag-card-${flag.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="text-uf-text font-semibold">{flag.member_name}</p>
                    <p className="text-uf-text-muted text-xs">{flag.member_number} | {flag.member_email}</p>
                  </div>
                  <div className={`px-2 py-1 rounded-md text-xs font-medium border ${getRiskBg(flag.risk_score)}`}>
                    <span className={getRiskColor(flag.risk_score)}>{flag.risk_score}%</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className={`px-2 py-0.5 rounded border ${getStatusBadge(flag.status)}`}>
                    {flag.status}
                  </span>
                  <span className="text-uf-text-muted flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {flag.unique_companions} companions
                  </span>
                  <span className="text-uf-text-muted">
                    {flag.total_paired_visits} paired visits
                  </span>
                  <ChevronRight className="w-3 h-3 text-uf-text-muted ml-auto" />
                </div>
              </div>
            ))
          )}
        </div>

        {/* Detail Panel */}
        {selectedFlag && (
          <div className="lg:col-span-2 bg-uf-surface border border-uf-border rounded-xl" data-testid="flag-detail">
            <div className="p-5 border-b border-uf-border">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-bold text-uf-text">{selectedFlag.member_name}</h3>
                  <p className="text-uf-text-muted text-sm">{selectedFlag.member_number} | {selectedFlag.member_email}</p>
                </div>
                <button onClick={() => setSelectedFlag(null)} className="text-uf-text-muted hover:text-uf-text">
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center gap-4 mt-3">
                <div className={`px-3 py-1.5 rounded-lg text-sm font-bold border ${getRiskBg(selectedFlag.risk_score)}`}>
                  <span className={getRiskColor(selectedFlag.risk_score)}>Risk: {selectedFlag.risk_score}%</span>
                </div>
                <span className={`px-2 py-1 rounded border text-xs font-medium ${getStatusBadge(selectedFlag.status)}`}>
                  {selectedFlag.status}
                </span>
                <span className="text-uf-text-muted text-xs">
                  Detected: {selectedFlag.detection_date ? new Date(selectedFlag.detection_date).toLocaleDateString('en-GB') : 'N/A'}
                </span>
              </div>
            </div>

            {/* Companion Patterns */}
            <div className="p-5 border-b border-uf-border">
              <h4 className="text-sm font-semibold text-uf-text mb-3 flex items-center gap-2">
                <Users className="w-4 h-4 text-uf-red" />
                Companion Patterns ({selectedFlag.unique_companions} unique)
              </h4>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(selectedFlag.pattern_summary?.companions || []).map((c, i) => (
                  <div key={i} className="bg-uf-black rounded-lg p-3 border border-uf-border">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-uf-text font-medium text-sm">{c.name}</span>
                        <span className="text-uf-text-muted text-xs ml-2">{c.member_number}</span>
                      </div>
                      <span className="text-uf-red text-xs font-bold">{c.total_paired_visits} paired visits</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs">
                      {Object.entries(c.days_pattern || {}).map(([day, count]) => (
                        <span key={day} className="bg-uf-surface-hi px-2 py-0.5 rounded text-uf-text-muted">
                          {day}: {count}x
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs text-uf-text-muted">
                      <Clock className="w-3 h-3" />
                      <span>Times: {(c.typical_times || []).join(', ')}</span>
                      <span className="ml-auto">{c.first_seen} — {c.last_seen}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Review Actions */}
            {selectedFlag.status !== 'confirmed' && selectedFlag.status !== 'dismissed' && (
              <div className="p-5 border-b border-uf-border">
                <h4 className="text-sm font-semibold text-uf-text mb-3">Review Actions</h4>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => setReviewModal({ flagId: selectedFlag.id, decision: 'dismissed', notes: '' })}
                    className="flex flex-col items-center gap-1.5 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg hover:bg-gray-500/20 transition-colors"
                    data-testid="action-dismiss"
                  >
                    <XCircle className="w-5 h-5 text-gray-400" />
                    <span className="text-xs text-gray-400 font-medium">Dismiss</span>
                  </button>
                  <button
                    onClick={() => setReviewModal({ flagId: selectedFlag.id, decision: 'monitoring', notes: '' })}
                    className="flex flex-col items-center gap-1.5 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg hover:bg-blue-500/20 transition-colors"
                    data-testid="action-monitor"
                  >
                    <Eye className="w-5 h-5 text-blue-400" />
                    <span className="text-xs text-blue-400 font-medium">Monitor</span>
                  </button>
                  <button
                    onClick={() => setReviewModal({ flagId: selectedFlag.id, decision: 'confirmed', notes: '' })}
                    className="flex flex-col items-center gap-1.5 p-3 bg-red-500/10 border border-red-500/20 rounded-lg hover:bg-red-500/20 transition-colors"
                    data-testid="action-confirm"
                  >
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="text-xs text-red-400 font-medium">Confirm</span>
                  </button>
                </div>
              </div>
            )}

            {/* Review History */}
            {selectedFlag.reviews?.length > 0 && (
              <div className="p-5">
                <h4 className="text-sm font-semibold text-uf-text mb-3">Review History</h4>
                <div className="space-y-2">
                  {selectedFlag.reviews.map(r => (
                    <div key={r.id} className="flex items-center gap-3 text-xs text-uf-text-muted">
                      <span className={`px-2 py-0.5 rounded border ${getStatusBadge(r.decision)}`}>{r.decision}</span>
                      <span>{r.reviewer_name}</span>
                      <span>{r.created_at ? new Date(r.created_at).toLocaleString('en-GB') : ''}</span>
                      {r.notes && <span className="text-uf-text italic">"{r.notes}"</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ──────────────── STAT CARD ────────────────

const StatCard = ({ label, value, icon: Icon, color, bg }) => (
  <div className="bg-uf-surface border border-uf-border rounded-xl p-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-uf-text-muted text-xs font-medium uppercase tracking-wider">{label}</span>
      <div className={`w-8 h-8 ${bg} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

// ──────────────── SETTINGS VIEW ────────────────

const SettingsView = ({ config, setConfig, saveConfig, savingConfig }) => {
  const update = (key, value) => setConfig(prev => ({ ...prev, [key]: value }));

  return (
    <div className="space-y-6" data-testid="settings-view">
      <div className="bg-uf-surface border border-uf-border rounded-xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-bold text-uf-text">Detection Engine Configuration</h3>
            <p className="text-uf-text-muted text-sm mt-1">
              Configure the rules and thresholds that the detection engine uses to identify unauthorized PT activity.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-sm text-uf-text-muted">Engine</span>
              <div
                onClick={() => update('enabled', !config.enabled)}
                className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${config.enabled ? 'bg-green-500' : 'bg-gray-600'}`}
                data-testid="engine-toggle"
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${config.enabled ? 'left-5' : 'left-0.5'}`} />
              </div>
              <span className={`text-sm font-medium ${config.enabled ? 'text-green-400' : 'text-gray-400'}`}>
                {config.enabled ? 'Active' : 'Disabled'}
              </span>
            </label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Proximity Window */}
          <SettingField
            label="Proximity Window"
            description="Maximum time gap (minutes) between two member entries to count as arriving together."
            value={config.proximity_window_minutes}
            onChange={v => update('proximity_window_minutes', parseInt(v) || 0)}
            suffix="minutes"
          />
          {/* Min Session Duration */}
          <SettingField
            label="Minimum Session Duration"
            description="Minimum time a member must stay in the gym for the visit to count as a potential PT session."
            value={config.min_session_duration_minutes}
            onChange={v => update('min_session_duration_minutes', parseInt(v) || 0)}
            suffix="minutes"
          />
          {/* Min Unique Companions */}
          <SettingField
            label="Minimum Unique Companions"
            description="Number of different people a member must be repeatedly seen with before being flagged."
            value={config.min_unique_companions}
            onChange={v => update('min_unique_companions', parseInt(v) || 0)}
            suffix="people"
          />
          {/* Min Occurrences Per Companion */}
          <SettingField
            label="Min. Occurrences Per Companion"
            description="How many times a member must arrive with the same companion before that pairing counts."
            value={config.min_occurrences_per_companion}
            onChange={v => update('min_occurrences_per_companion', parseInt(v) || 0)}
            suffix="times"
          />
          {/* Lookback Period */}
          <SettingField
            label="Lookback Period"
            description="How far back in time the engine analyses access logs."
            value={config.lookback_days}
            onChange={v => update('lookback_days', parseInt(v) || 0)}
            suffix="days"
          />
          {/* Analysis Hour */}
          <SettingField
            label="Scheduled Analysis Time"
            description="Hour of the day (24h, UTC) the automated daily analysis runs."
            value={config.analysis_hour}
            onChange={v => update('analysis_hour', parseInt(v) || 0)}
            suffix="h (UTC)"
          />
          {/* Confidence Threshold */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-uf-text">Confidence Threshold</label>
            <p className="text-xs text-uf-text-muted">
              Minimum risk score (0-100) required to create a flag. Lower values = more sensitive.
            </p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="100"
                value={config.confidence_threshold * 100}
                onChange={e => update('confidence_threshold', parseInt(e.target.value) / 100)}
                className="flex-1 accent-uf-red"
                data-testid="confidence-slider"
              />
              <span className="text-uf-text font-medium text-sm w-12 text-right">
                {config.confidence_threshold != null ? Math.round(config.confidence_threshold * 100) : 60}%
              </span>
            </div>
          </div>
        </div>

        {/* Exclusion toggles */}
        <div className="mt-6 pt-6 border-t border-uf-border">
          <h4 className="text-sm font-semibold text-uf-text mb-4">Exclusions</h4>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.exclude_registered_pts}
                onChange={e => update('exclude_registered_pts', e.target.checked)}
                className="accent-uf-red w-4 h-4"
                data-testid="exclude-pts-toggle"
              />
              <span className="text-sm text-uf-text">Exclude registered PTs</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={config.exclude_staff}
                onChange={e => update('exclude_staff', e.target.checked)}
                className="accent-uf-red w-4 h-4"
                data-testid="exclude-staff-toggle"
              />
              <span className="text-sm text-uf-text">Exclude staff members</span>
            </label>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={saveConfig}
            disabled={savingConfig}
            className="px-6 py-2 bg-uf-red hover:bg-uf-red-hover text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            data-testid="save-config-btn"
          >
            {savingConfig ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SettingField = ({ label, description, value, onChange, suffix }) => (
  <div className="space-y-2">
    <label className="text-sm font-medium text-uf-text">{label}</label>
    <p className="text-xs text-uf-text-muted">{description}</p>
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-24 bg-uf-black border border-uf-border rounded-lg px-3 py-2 text-sm"
        style={{ color: '#EDEDED' }}
        data-testid={`setting-${label.toLowerCase().replace(/\s/g, '-')}`}
      />
      <span className="text-uf-text-muted text-sm">{suffix}</span>
    </div>
  </div>
);

// ──────────────── RUN HISTORY VIEW ────────────────

const RunHistoryView = ({ runs }) => (
  <div className="space-y-4" data-testid="run-history">
    <div className="bg-uf-surface border border-uf-border rounded-xl overflow-hidden">
      <div className="p-5 border-b border-uf-border">
        <h3 className="text-lg font-bold text-uf-text">Analysis Run History</h3>
        <p className="text-uf-text-muted text-sm mt-1">Log of all automated and manual detection runs.</p>
      </div>
      <div className="admin-table-wrap">
        <table className="w-full">
          <thead>
            <tr className="border-b border-uf-border text-left">
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Date/Time</th>
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Trigger</th>
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Status</th>
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Logs</th>
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Members</th>
              <th className="px-5 py-3 text-xs font-semibold text-uf-text-muted uppercase tracking-wider">Flags</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-8 text-center text-uf-text-muted text-sm">
                  No analysis runs yet. Click "Run Analysis" to start.
                </td>
              </tr>
            ) : (
              runs.map(run => (
                <tr key={run.id} className="border-b border-uf-border/50 hover:bg-uf-surface-hi/50">
                  <td className="px-5 py-3 text-sm text-uf-text">
                    {run.started_at ? new Date(run.started_at).toLocaleString('en-GB') : '—'}
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      run.triggered_by === 'scheduled' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {run.triggered_by}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                      run.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      run.status === 'running' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {run.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-sm text-uf-text-muted">{run.logs_processed}</td>
                  <td className="px-5 py-3 text-sm text-uf-text-muted">{run.members_analyzed}</td>
                  <td className="px-5 py-3 text-sm text-uf-text font-medium">{run.flags_created}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  </div>
);

export default PTDetectionDashboard;
